// Drug Code is the 3-character segment of a catalog Product ID (PRX|FLX|2000|CA).
// Generated codes are letters only, so there are exactly 26^3 of them and no digit
// escape hatch -- which is why an exhausted ladder falls through to a namespace
// scan rather than a numeric suffix. An existing or hand-typed code may also carry
// digits after its first letter, e.g. TB-500's TB5 (see drugCodeAllowedValueRegex).
export const DRUG_CODE_LENGTH = 3;

export const DRUG_CODE_NAMESPACE_SIZE = 26 ** DRUG_CODE_LENGTH; // 17,576

const VOWELS = 'AEIOU';

const lettersOf = (drugName) =>
  String(drugName ?? '')
    .toUpperCase()
    .replace(/[^A-Z]/g, '');

export const toTakenCodeSet = (takenCodes) =>
  new Set((takenCodes ?? []).map((code) => String(code).trim().toUpperCase()));

// Drug names arrive with inconsistent case, spacing and punctuation ("Vitamin D3"
// / "vitamin-d3"), none of which changes which drug is meant. Must stay identical
// to DrugCodeMappingService.normalizeDrugNameForMatch in smartphos-core -- the
// backend rejects a code that disagrees with the mapping it finds under its own
// normalization, so a looser rule here would autofill a code the server refuses.
export const normalizeDrugNameForMatch = (drugName) =>
  String(drugName ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

// The drug_code_mapping rows, indexed for the two questions the dialog asks of
// them: which code does this drug already have, and which codes are spoken for.
export const toDrugCodeIndex = (mappings) => {
  const codeByName = new Map();
  const canonicalNameByName = new Map();
  const takenCodes = new Set();

  (mappings ?? []).forEach(({ drugName, drugCode } = {}) => {
    const code = String(drugCode ?? '')
      .trim()
      .toUpperCase();
    const key = normalizeDrugNameForMatch(drugName);

    if (!code) return;

    takenCodes.add(code);

    if (key) {
      codeByName.set(key, code);
      // The registry's own spelling, shown back to the user so a locked field
      // explains itself: the drug is already known, under this name.
      canonicalNameByName.set(key, String(drugName).trim());
    }
  });

  return { codeByName, canonicalNameByName, takenCodes };
};

// Accepts either a raw list from the API or a Set already normalized by
// toTakenCodeSet, so callers don't have to rebuild it on every keystroke.
const asCodeSet = (takenCodes) =>
  takenCodes instanceof Set ? takenCodes : toTakenCodeSet(takenCodes);

// Ordered candidates, most drug-like first, so a collision still yields a code
// that reads like the drug instead of an arbitrary triple.
export const buildDrugCodeCandidates = (drugName) => {
  const letters = lettersOf(drugName);
  if (letters.length < DRUG_CODE_LENGTH) return [];

  const first = letters[0];
  const rest = letters.slice(1);
  const consonants = [...rest].filter((letter) => !VOWELS.includes(letter));

  const candidates = [];

  // First letter + next two consonants -- Fluoxetine -> FLX, Anastrozole -> ANS.
  if (consonants.length >= 2) candidates.push(first + consonants[0] + consonants[1]);

  // First three letters -- Anastrozole -> ANA, Atorvastatin -> ATO.
  candidates.push(letters.slice(0, DRUG_CODE_LENGTH));

  // First two letters + each later letter -- FLO, FLE, FLT, FLI, FLN.
  for (let i = 1; i < rest.length; i += 1) candidates.push(first + rest[0] + rest[i]);

  // First letter + each ordered later pair. Widens the pool well past the
  // obvious codes before the scan has to take over.
  for (let i = 0; i < rest.length; i += 1) {
    for (let j = i + 1; j < rest.length; j += 1) candidates.push(first + rest[i] + rest[j]);
  }

  return [...new Set(candidates)];
};

const codeAtIndex = (index) =>
  String.fromCharCode(65 + Math.floor(index / 676)) +
  String.fromCharCode(65 + (Math.floor(index / 26) % 26)) +
  String.fromCharCode(65 + (index % 26));

// Seeded from the drug name so two drugs that both fall through to the scan
// don't start at the same place and walk the same stretch of the namespace.
const seedFor = (letters) =>
  [...letters].reduce(
    (seed, letter) => (seed * 31 + letter.charCodeAt(0)) % DRUG_CODE_NAMESPACE_SIZE,
    0
  );

// Last resort once every name-derived candidate is taken. Codes are consumed per
// catalog row, so the drug-like candidates run out long before the namespace
// does; without this the field would simply come back empty.
export const scanNamespace = (drugName, isTaken) => {
  const seed = seedFor(lettersOf(drugName));

  for (let offset = 0; offset < DRUG_CODE_NAMESPACE_SIZE; offset += 1) {
    const code = codeAtIndex((seed + offset) % DRUG_CODE_NAMESPACE_SIZE);
    if (!isTaken(code)) return code;
  }

  return null;
};

export const isDrugCodeTaken = (drugCode, takenCodes) =>
  asCodeSet(takenCodes).has(
    String(drugCode ?? '')
      .trim()
      .toUpperCase()
  );

// First free code for this drug name. Returns null when the name has fewer than
// 3 letters (e.g. "B12") or every code is in use -- the dialog leaves the field
// empty and editable in both cases, which is the pre-existing behaviour.
export const resolveDrugCode = (drugName, takenCodes) => {
  const taken = asCodeSet(takenCodes);
  const isTaken = (code) => taken.has(code);

  const candidates = buildDrugCodeCandidates(drugName);
  if (!candidates.length) return null;

  return candidates.find((code) => !isTaken(code)) ?? scanNamespace(drugName, isTaken);
};

// The code this drug should carry, and whether the registry already decided it.
//
// A mapped drug keeps its code -- which is the whole point of the mapping table.
// Generation used to be the only path, and because it picks the first code *not*
// already in use, a drug's own code always looked taken: Fluoxetine got FLX the
// first time and FLO the next. `isExisting` tells the dialog to lock the field so
// that can't be undone by hand.
export const resolveDrugCodeForName = (
  drugName,
  { codeByName, canonicalNameByName, takenCodes } = {}
) => {
  const key = normalizeDrugNameForMatch(drugName);
  const mapped = codeByName?.get(key);

  if (mapped) {
    return {
      drugCode: mapped,
      isExisting: true,
      canonicalDrugName: canonicalNameByName?.get(key) ?? String(drugName).trim(),
    };
  }

  return {
    drugCode: resolveDrugCode(drugName, takenCodes) ?? '',
    isExisting: false,
    canonicalDrugName: '',
  };
};
